import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Typography, Box, Grid, Paper, TextField, IconButton, Tooltip } from '@mui/material';
import { Timeline, TimelineItem, TimelineSeparator, TimelineConnector, TimelineContent, TimelineDot, TimelineOppositeContent } from '@mui/lab';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import GetAppIcon from '@mui/icons-material/GetApp';
import DescriptionIcon from '@mui/icons-material/Description';
import { Carousel } from 'react-responsive-carousel'; // Import Carousel component
import 'react-responsive-carousel/lib/styles/carousel.min.css'; // Import Carousel styles
import { PieChart, Pie, Cell, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts'; // Import PieChart components
import jsPDF from 'jspdf'; // Import jsPDF for PDF export
import html2canvas from 'html2canvas'; // Import html2canvas for capturing the screen
import * as XLSX from 'xlsx'; // Import XLSX for Excel export
import { Document, Packer, Paragraph, TextRun } from 'docx'; // Import docx for Word export
import { saveAs } from 'file-saver'; // Import file-saver for saving files
import config from '../config/config'; // Import configuration
import { formatDate } from '../utils/formatterUtils'; // Import formatDate
import './TimelinePopup.css'; // Import custom CSS for carousel arrows

const TimelinePopupComponent = ({ projectId }) => {
  const [milestones, setMilestones] = useState([]);
  const [tasks, setTasks] = useState({});
  const [taskStatusData, setTaskStatusData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [milestoneImages, setMilestoneImages] = useState({});
  const contentRef = useRef(); // Ref for the content to be captured
  const toolbarRef = useRef(); // Ref for the toolbar to be excluded

  useEffect(() => {
    const fetchMilestones = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const response = await axios.get(`${config.apiBaseUrl}/api/milestones/project/${projectId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-TenantID': 'admin',
          },
        });
        setMilestones(response.data);
        // Fetch tasks and images for each milestone
        await Promise.all(response.data.map(milestone => fetchTasks(milestone.id)));
      } catch (error) {
        console.error('Error fetching milestones:', error);
      }
    };

    const fetchTasks = async (milestoneId) => {
      try {
        const token = localStorage.getItem('authToken');
        const response = await axios.get(`${config.apiBaseUrl}/api/tasks/milestone/${milestoneId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-TenantID': 'admin',
          },
        });
        const tasks = response.data;
        setTasks((prevTasks) => {
          const updatedTasks = { ...prevTasks, [milestoneId]: tasks };
          return updatedTasks;
        });
        // Fetch images for each task
        await Promise.all(tasks.map(task => fetchImages(task.id, milestoneId)));
      } catch (error) {
        console.error('Error fetching tasks:', error);
      }
    };

    if (projectId) {
      fetchMilestones();
    }
  }, [projectId]);

  useEffect(() => {
    const updateTaskStatusData = () => {
      const statusCounts = {
        'In Progress': 0,
        'Completed': 0,
        'Delayed': 0,
      };

      Object.values(tasks).flat().forEach((task) => {
        if (statusCounts[task.status] !== undefined) {
          statusCounts[task.status] += 1;
        }
      });

      const data = Object.keys(statusCounts).map((status) => ({
        name: status,
        value: statusCounts[status],
      }));

      setTaskStatusData(data);
    };

    updateTaskStatusData();
  }, [tasks]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'In Progress':
        return 'orange';
      case 'Completed':
        return 'green';
      case 'Delayed':
        return 'lightcoral';
      default:
        return 'black';
    }
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

  // Dummy image URLs
  const dummyImages = [
    'https://via.placeholder.com/600x400?text=Image+1',
    'https://via.placeholder.com/600x400?text=Image+2',
    'https://via.placeholder.com/600x400?text=Image+3',
    'https://via.placeholder.com/600x400?text=Image+4',
    'https://via.placeholder.com/600x400?text=Image+5',
  ];

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const filteredMilestones = milestones
    .filter(milestone => milestone.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate)); // Sort milestones by date

  const exportToPDF = () => {
    const content = contentRef.current;
    const toolbar = toolbarRef.current;
    if (!content || !toolbar) {
      console.error('Content or toolbar reference is not available.');
      return;
    }
    const originalHeight = content.style.height;
    const originalOverflow = content.style.overflow;
    const originalDisplay = toolbar.style.display;

    toolbar.style.display = 'none'; // Hide the toolbar
    content.style.height = 'auto'; // Temporarily set height to auto to capture full content
    content.style.overflow = 'visible'; // Ensure overflow content is visible

    setTimeout(() => {
      html2canvas(content, { useCORS: true }).then((canvas) => {
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF();
        const imgProps = pdf.getImageProperties(imgData);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
        pdf.save('timeline.pdf');
        toolbar.style.display = originalDisplay; // Restore original display
        content.style.height = originalHeight; // Restore original height
        content.style.overflow = originalOverflow; // Restore original overflow
      });
    }, 1000); // Add a delay to ensure content is fully rendered
  };

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredMilestones);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Milestones');
    XLSX.writeFile(workbook, 'timeline.xlsx');
  };

  const exportToWord = () => {
    const doc = new Document({
      sections: [
        {
          children: filteredMilestones.map(milestone => 
            new Paragraph({
              children: [
                new TextRun({
                  text: milestone.name,
                  bold: true,
                }),
                new Paragraph(milestone.description),
              ],
            })
          ),
        },
      ],
    });
    Packer.toBlob(doc).then(blob => {
      saveAs(blob, 'timeline.docx');
    });
  };

  const fetchImages = async (taskId, milestoneId) => {
    try {
      const response = await axios.get(`${config.apiBaseUrl}/api/tasks/${taskId}/images`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'X-TenantID': 'admin',
        },
      });
      const taskImages = response.data;

      // Download each image
      const downloadedImages = await Promise.all(taskImages.map(async (taskImage) => {
        const fileName = taskImage.imageUrl.split('/').pop(); // Extract the file name from the URL
        console.log(`Milestone ID: ${milestoneId}, Task ID: ${taskId}, Image Name: ${fileName}`); // Debug statement
        try {
          const downloadResponse = await axios.get(`${config.apiBaseUrl}/api/tasks/${taskId}/downloadImage/${fileName}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
              'X-TenantID': 'admin',
            },
            responseType: 'blob',
          });

          if (downloadResponse.status === 200) {
            const imageUrl = URL.createObjectURL(downloadResponse.data);
            return { ...taskImage, imageUrl };
          } else {
            console.error(`Failed to download image ${fileName} with status code: ${downloadResponse.status}`);
            return null;
          }
        } catch (downloadError) {
          console.error(`Error downloading image ${fileName}:`, downloadError);
          return null;
        }
      }));

      setMilestoneImages((prevImages) => ({
        ...prevImages,
        [milestoneId]: (prevImages[milestoneId] || []).concat(downloadedImages.filter(image => image !== null)),
      }));
    } catch (error) {
      console.error('Error fetching images:', error);
      if (error.response) {
        console.error('Error response:', error.response);
      }
      setMilestoneImages((prevImages) => ({
        ...prevImages,
        [milestoneId]: [],
      }));
    }
  };

  return (
    <Box ref={contentRef}>
      <Box ref={toolbarRef} display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <TextField
          label="Search Milestones"
          variant="outlined"
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <Box>
          <Tooltip title="Export PDF">
            <IconButton onClick={exportToPDF} sx={{ color: '#FF0000' }}>
              <PictureAsPdfIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export Excel">
            <IconButton onClick={exportToExcel} sx={{ color: '#217346' }}>
              <GetAppIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export Word">
            <IconButton onClick={exportToWord} sx={{ color: '#2B579A' }}>
              <DescriptionIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Paper elevation={3} sx={{ padding: 2, height: '100%' }}>
            <Box textAlign="center">
              <Typography variant="h6" gutterBottom sx={{ fontSize: '0.875rem' }}>Milestone Timeline</Typography>
              <Timeline sx={{ width: '100%' }}>
                {filteredMilestones.map((milestone) => (
                  <TimelineItem key={milestone.id}>
                    <TimelineOppositeContent sx={{ flex: 0.2 }}>
                      <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                        {formatDate(milestone.dueDate, config.defaultLocale)}
                      </Typography>
                    </TimelineOppositeContent>
                    <TimelineSeparator>
                      <TimelineDot sx={{ bgcolor: getStatusColor(milestone.status) }} />
                      <TimelineConnector sx={{ bgcolor: getStatusColor(milestone.status) }} />
                    </TimelineSeparator>
                    <TimelineContent sx={{ flex: 0.8 }}>
                      <Typography variant="h6" component="h1" sx={{ fontSize: '0.875rem' }}>
                        {milestone.name}
                      </Typography>
                    </TimelineContent>
                  </TimelineItem>
                ))}
              </Timeline>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={6}>
          <Paper elevation={3} sx={{ padding: 2, height: '100%' }}>
            <Box textAlign="center" height="100%" display="flex" flexDirection="column" justifyContent="center" alignItems="center">
              <Typography variant="h6" gutterBottom>Tasks Status</Typography>
              <Box width="100%" height="100%">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={taskStatusData}
                      cx="50%"
                      cy="50%"
                      label={({ name, value }) => `${name}: ${value}`}
                      labelLine={false}
                      outerRadius="80%"
                      fill="#8884d8"
                      dataKey="value"
                      animationDuration={500} // Reduce animation speed
                    >
                      {taskStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ padding: 2 }}>
            <Typography variant="h6" align="center" gutterBottom>Milestone Details</Typography>
            {filteredMilestones.map((milestone) => (
              <React.Fragment key={milestone.id}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box mt={2}>
                      <Typography variant="h6" align="center">{milestone.name} - Images</Typography>
                      <Carousel
                        showThumbs={false}
                        showStatus={false}
                        infiniteLoop
                        useKeyboardArrows
                      >
                        {(milestoneImages[milestone.id] && milestoneImages[milestone.id].length > 0 ? milestoneImages[milestone.id] : dummyImages).map((image, index) => (
                          <div key={index}>
                            <img src={typeof image === 'string' ? image : image.imageUrl} alt={`Image ${index + 1}`} style={{ width: '100%', height: '300px', objectFit: 'contain' }} />
                          </div>
                        ))}
                      </Carousel>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Timeline>
                      <TimelineItem>
                        <TimelineOppositeContent>
                          <Typography variant="body2" color="textSecondary">
                            {formatDate(milestone.dueDate, config.defaultLocale)}
                          </Typography>
                        </TimelineOppositeContent>
                        <TimelineSeparator>
                          <TimelineDot sx={{ bgcolor: getStatusColor(milestone.status) }} />
                          <TimelineConnector sx={{ bgcolor: getStatusColor(milestone.status) }} />
                        </TimelineSeparator>
                        <TimelineContent>
                          <Typography variant="h6" component="h1">
                            {milestone.name}
                          </Typography>
                          <Typography>{milestone.description}</Typography>
                          <Box mt={2}>
                            <Typography variant="subtitle1">Tasks</Typography>
                            {tasks[milestone.id] && tasks[milestone.id].map((task) => (
                              <Box key={task.id} mb={1}>
                                <Typography variant="body2" style={{ color: getStatusColor(task.status) }}>
                                  {task.name} - {task.status}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                  Assigned To: {task.assignedTo}, Start Date: {formatDate(task.startDate, config.defaultLocale)}, End Date: {formatDate(task.endDate, config.defaultLocale)}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </TimelineContent>
                      </TimelineItem>
                    </Timeline>
                  </Grid>
                </Grid>
              </React.Fragment>
            ))}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TimelinePopupComponent;